import { useSession } from "@/services/auth0/session";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import type {
  CustomerContact,
  CreateContactInput,
  UpdateContactInput,
  ListContactsQuery,
  ListContactsResponse,
  ParsedContactData,
} from "./types";

const API_URL = import.meta.env.VITE_API_URL;

// Query keys
export const contactsKeys = {
  all: ["contacts"] as const,
  lists: () => [...contactsKeys.all, "list"] as const,
  list: (filters: ListContactsQuery) =>
    [...contactsKeys.lists(), filters] as const,
  details: () => [...contactsKeys.all, "detail"] as const,
  detail: (id: string) => [...contactsKeys.details(), id] as const,
};

// List contacts
export const useContacts = (
  query?: ListContactsQuery & { customerId?: string }
) => {
  const { token } = useSession();

  return useQuery({
    queryKey: contactsKeys.list(query ?? {}),
    queryFn: async (): Promise<ListContactsResponse> => {
      const params = new URLSearchParams();

      if (query?.search) params.append("search", query.search);
      if (query?.isFavorite !== undefined)
        params.append("isFavorite", String(query.isFavorite));
      if (query?.label) params.append("label", query.label);
      if (query?.customerId) params.append("customerId", query.customerId);
      if (query?.limit) params.append("limit", String(query.limit));
      if (query?.offset) params.append("offset", String(query.offset));

      const { data } = await axios.get<ListContactsResponse>(
        `${API_URL}/api/v2/address-book?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
  });
};

// Get single contact
export const useContact = (id: string) => {
  const { token } = useSession();

  return useQuery({
    queryKey: contactsKeys.detail(id),
    queryFn: async (): Promise<CustomerContact> => {
      const { data } = await axios.get<CustomerContact>(
        `${API_URL}/api/v2/address-book/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    enabled: !!id,
  });
};

// Create contact
export const useCreateContact = () => {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      customerId,
      input,
    }: {
      customerId: string;
      input: CreateContactInput;
    }): Promise<CustomerContact> => {
      const { data } = await axios.post<CustomerContact>(
        `${API_URL}/api/v2/customers/${customerId}/address-book`,
        input,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: contactsKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: ["customers", variables.customerId, "contacts"],
      });
    },
  });
};

// Update contact
export const useUpdateContact = () => {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      customerId,
      id,
      input,
    }: {
      customerId: string;
      id: string;
      input: UpdateContactInput;
    }): Promise<CustomerContact> => {
      const { data } = await axios.patch<CustomerContact>(
        `${API_URL}/api/v2/customers/${customerId}/address-book/${id}`,
        input,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: contactsKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: contactsKeys.detail(variables.id),
      });
      void queryClient.invalidateQueries({
        queryKey: ["customers", variables.customerId, "contacts"],
      });
    },
  });
};

// Delete contact
export const useDeleteContact = () => {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      customerId,
      id,
    }: {
      customerId: string;
      id: string;
    }): Promise<void> => {
      await axios.delete(
        `${API_URL}/api/v2/customers/${customerId}/address-book/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: contactsKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: ["customers", variables.customerId, "contacts"],
      });
    },
  });
};

// Set default contact
export const useSetDefaultContact = () => {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      customerId,
      id,
    }: {
      customerId: string;
      id: string;
    }): Promise<CustomerContact> => {
      const { data } = await axios.patch<CustomerContact>(
        `${API_URL}/api/v2/customers/${customerId}/address-book/${id}/set-default`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: contactsKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: ["customers", variables.customerId, "contacts"],
      });
    },
  });
};

// Toggle favorite
export const useToggleFavoriteContact = () => {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      customerId,
      id,
    }: {
      customerId: string;
      id: string;
    }): Promise<CustomerContact> => {
      const { data } = await axios.patch<CustomerContact>(
        `${API_URL}/api/v2/customers/${customerId}/address-book/${id}/toggle-favorite`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({ queryKey: contactsKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: contactsKeys.detail(data.id),
      });
      void queryClient.invalidateQueries({
        queryKey: ["customers", variables.customerId, "contacts"],
      });
    },
  });
};

// List contacts for a customer (works for both admin and customer via middleware)
export const useCustomerContacts = (
  customerId: string,
  query?: ListContactsQuery
) => {
  const { token } = useSession();

  return useQuery({
    queryKey: ["customers", customerId, "contacts", query],
    queryFn: async (): Promise<ListContactsResponse> => {
      const params = new URLSearchParams();

      if (query?.search) params.append("search", query.search);
      if (query?.isFavorite !== undefined)
        params.append("isFavorite", String(query.isFavorite));
      if (query?.label) params.append("label", query.label);
      if (query?.limit) params.append("limit", String(query.limit));
      if (query?.offset) params.append("offset", String(query.offset));

      const { data } = await axios.get<ListContactsResponse>(
        `${API_URL}/api/v2/customers/${customerId}/address-book?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    enabled: !!customerId,
  });
};

// Parse contact input using AI
export const useParseContact = () => {
  const { token } = useSession();

  return useMutation({
    mutationFn: async (input: string): Promise<ParsedContactData> => {
      const { data } = await axios.post<ParsedContactData>(
        `${API_URL}/api/v2/address-book/parse`,
        { input },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
  });
};
